import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, ACTIVITIES_PRIMARY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, Send, CalendarPlus, LogOut, Users, Loader2, Clock, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function GroupRoom() {
  const { t, isRTL } = useLang();
  const { user } = useAuth();
  const { groupId } = useParams();
  const nav = useNavigate();
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  // Event creation dialog state
  const [evOpen, setEvOpen] = useState(false);
  const [evForm, setEvForm] = useState({ title: "", description: "", category: "Dining", date: new Date(), time: "19:00" });
  const [creating, setCreating] = useState(false);
  const [rsvpBusy, setRsvpBusy] = useState(null);

  const load = async () => {
    try {
      const [g, m] = await Promise.all([
        api.get(`/groups/${groupId}`),
        api.get(`/groups/${groupId}/messages`),
      ]);
      setGroup(g.data);
      setMessages(m.data);
    } catch (e) {
      if (e?.response?.status === 403) nav("/groups");
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [groupId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const txt = text.trim();
    if (!txt) return;
    setText("");
    try {
      const res = await api.post(`/groups/${groupId}/messages`, { text: txt });
      setMessages((m) => [...m, res.data]);
    } catch {}
  };

  const leave = async () => {
    try {
      await api.post(`/groups/${groupId}/leave`);
      nav("/groups");
    } catch {}
  };

  const createEvent = async () => {
    if (!evForm.title.trim()) { toast.error(t("titleRequired")); return; }
    setCreating(true);
    try {
      const [h, m] = (evForm.time || "19:00").split(":").map((x) => parseInt(x, 10) || 0);
      const dt = new Date(evForm.date);
      dt.setHours(h, m, 0, 0);
      const res = await api.post(`/groups/${groupId}/events`, {
        title: evForm.title.trim(),
        description: evForm.description.trim(),
        category: evForm.category,
        scheduled_at: dt.toISOString(),
      });
      setMessages((prev) => [...prev, res.data]);
      toast.success(t("eventCreated"));
      setEvOpen(false);
      setEvForm({ title: "", description: "", category: "Dining", date: new Date(), time: "19:00" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
    } finally { setCreating(false); }
  };

  const rsvp = async (msgId, status) => {
    setRsvpBusy(msgId + ":" + status);
    try {
      const res = await api.post(`/groups/${groupId}/events/${msgId}/rsvp`, { status });
      setMessages((prev) => prev.map((mm) => (mm.id === msgId ? res.data : mm)));
      toast.success(status === "accepted" ? t("accept") : t("reject"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("networkErr"));
    } finally { setRsvpBusy(null); }
  };

  if (!group) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const isOwner = group.owner_id === user.id;
  const memberNames = (mids) => {
    const m = new Map((group.members || []).map((u) => [u.id, u.alias]));
    return (mids || []).map((id) => m.get(id) || "?");
  };

  return (
    <div className="max-w-3xl mx-auto h-[calc(100vh-64px)] flex flex-col" data-testid="group-room">
      <div className="glass border-b border-[hsl(var(--border))] px-4 py-3 flex items-center gap-3 sticky top-0">
        <Button variant="ghost" size="icon" onClick={() => nav("/groups")} data-testid="group-back" className="rounded-full">
          <ArrowLeft className={`h-5 w-5 ${isRTL ? "rotate-180" : ""}`} />
        </Button>
        <div className="h-10 w-10 rounded-2xl bg-[hsl(var(--primary))] text-white flex items-center justify-center font-semibold">
          {group.name?.slice(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{group.name}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1">
            <Users className="h-3 w-3" /> {group.members?.length || group.member_ids.length} {t("of")} {group.max_members}
          </div>
        </div>
        {isOwner && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEvOpen(true)}
            data-testid="group-create-event-btn"
            className="rounded-full border-[hsl(var(--border))] gap-1.5"
          >
            <CalendarPlus className="h-4 w-4 text-[hsl(var(--primary))]" />
            <span className="hidden sm:inline">{t("createEvent")}</span>
          </Button>
        )}
        <Button variant="ghost" size="icon" onClick={leave} data-testid="group-leave-btn" className="rounded-full text-[hsl(var(--destructive))]">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-2" data-testid="group-messages">
        {messages.map((m) => {
          const mine = m.from_id === user.id;
          if (m.type === "event") {
            const ev = m.event || {};
            const rsvps = m.rsvps || {};
            const myStatus = rsvps[user.id];
            const accepted = Object.entries(rsvps).filter(([, s]) => s === "accepted").map(([id]) => id);
            const declined = Object.entries(rsvps).filter(([, s]) => s === "declined").map(([id]) => id);
            const dt = new Date(ev.scheduled_at);
            return (
              <div key={m.id} className="flex justify-center" data-testid={`event-msg-${m.id}`}>
                <div className="w-full max-w-md bg-white border-2 border-[hsl(var(--primary))] rounded-2xl p-4 shadow-[0_4px_18px_rgba(22,200,90,0.15)]">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--primary))]">
                    <CalendarPlus className="h-3.5 w-3.5" />
                    {t("newEvent")} · {m.from_alias}
                  </div>
                  <div className="mt-2 font-heading text-lg font-semibold">{ev.title}</div>
                  {ev.description && <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">{ev.description}</div>}
                  <div className="mt-3 flex flex-wrap gap-3 text-xs">
                    <span className="px-2 py-1 rounded-full bg-[hsl(var(--elevated))] border border-[hsl(var(--border))] font-medium">{ev.category}</span>
                    <span className="inline-flex items-center gap-1 text-[hsl(var(--muted-foreground))]">
                      <Clock className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
                      {dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>

                  {myStatus ? (
                    <div className={`mt-3 px-3 py-2 rounded-xl text-sm font-medium ${myStatus === "accepted" ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>
                      {myStatus === "accepted" ? `✓ ${t("youAccepted")}` : `✗ ${t("youDeclined")}`}
                      <button
                        onClick={() => rsvp(m.id, myStatus === "accepted" ? "declined" : "accepted")}
                        data-testid={`rsvp-toggle-${m.id}`}
                        className="ms-2 underline text-xs font-normal"
                      >
                        {myStatus === "accepted" ? t("reject") : t("accept")}
                      </button>
                    </div>
                  ) : (
                    <div className="mt-3 flex gap-2">
                      <Button
                        onClick={() => rsvp(m.id, "accepted")}
                        disabled={rsvpBusy === m.id + ":accepted"}
                        data-testid={`rsvp-accept-${m.id}`}
                        size="sm"
                        className="flex-1 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white"
                      >
                        {rsvpBusy === m.id + ":accepted" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        <span className="ms-1">{t("accept")}</span>
                      </Button>
                      <Button
                        onClick={() => rsvp(m.id, "declined")}
                        disabled={rsvpBusy === m.id + ":declined"}
                        data-testid={`rsvp-decline-${m.id}`}
                        size="sm"
                        variant="outline"
                        className="flex-1 rounded-full border-[hsl(var(--border))]"
                      >
                        <X className="h-4 w-4 me-1" />
                        {t("reject")}
                      </Button>
                    </div>
                  )}

                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                    <div className="rounded-xl bg-[hsl(var(--elevated))] p-2">
                      <div className="text-[hsl(var(--primary))] font-bold uppercase tracking-wider">{accepted.length} {t("going")}</div>
                      <div className="text-[hsl(var(--muted-foreground))] truncate">{memberNames(accepted).join(", ") || "—"}</div>
                    </div>
                    <div className="rounded-xl bg-[hsl(var(--muted))] p-2">
                      <div className="text-[hsl(var(--muted-foreground))] font-bold uppercase tracking-wider">{declined.length} {t("declined")}</div>
                      <div className="text-[hsl(var(--muted-foreground))] truncate">{memberNames(declined).join(", ") || "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[78%] px-4 py-2 rounded-2xl text-sm leading-relaxed ${
                  mine
                    ? "bg-[hsl(var(--secondary))] text-white bubble-out"
                    : "bg-white border border-[hsl(var(--border))] text-[hsl(var(--foreground))] bubble-in"
                }`}
              >
                {!mine && (
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--primary))] mb-0.5">
                    {m.from_alias}
                  </div>
                )}
                {m.text}
                <div className={`mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}`}>
                  {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="border-t border-[hsl(var(--border))] bg-white px-4 py-3 flex items-center gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("typeMessage")}
          className="rounded-full h-11 bg-[hsl(var(--elevated))] border-transparent focus-visible:ring-1 focus-visible:ring-[hsl(var(--primary))]"
          data-testid="group-msg-input"
        />
        <Button type="submit" size="icon" className="h-11 w-11 rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white shrink-0" data-testid="group-msg-send">
          <Send className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} />
        </Button>
      </form>

      {/* Create Event Dialog */}
      <Dialog open={evOpen} onOpenChange={setEvOpen}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="create-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-[hsl(var(--primary))]" />
              {t("createEvent")}
            </DialogTitle>
            <DialogDescription>{group.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">{t("groupName") /* re-using as label */}</Label>
            </div>
            <div>
              <Label className="text-sm">{t("title")}</Label>
              <Input
                value={evForm.title}
                onChange={(e) => setEvForm({ ...evForm, title: e.target.value })}
                placeholder={t("eventTitlePlaceholder")}
                className="mt-1.5 rounded-xl h-10"
                data-testid="ev-title-input"
              />
            </div>
            <div>
              <Label className="text-sm">{t("description")}</Label>
              <Textarea
                value={evForm.description}
                onChange={(e) => setEvForm({ ...evForm, description: e.target.value })}
                className="mt-1.5 rounded-xl"
                rows={2}
                data-testid="ev-desc-input"
              />
            </div>
            <div>
              <Label className="text-sm">{t("activity")}</Label>
              <Select value={evForm.category} onValueChange={(v) => setEvForm({ ...evForm, category: v })}>
                <SelectTrigger className="mt-1.5 rounded-xl h-10" data-testid="ev-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITIES_PRIMARY.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">{t("date")}</Label>
              <div className="mt-1.5 rounded-2xl border border-[hsl(var(--border))] p-2">
                <CalendarPicker
                  mode="single"
                  selected={evForm.date}
                  onSelect={(d) => d && setEvForm({ ...evForm, date: d })}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  className="w-full"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">{t("time")}</Label>
              <Input
                type="time"
                value={evForm.time}
                onChange={(e) => setEvForm({ ...evForm, time: e.target.value })}
                className="mt-1.5 rounded-xl h-10"
                data-testid="ev-time-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={createEvent}
              disabled={creating}
              data-testid="ev-create-btn"
              className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white gap-1.5"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {t("createEvent")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
